import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/constants";
import { XCircle, Loader2 } from "lucide-react";

// ITEM 22 — the four endings. Each carries a DIFFERENT successor; the modal shows what each one does
// so a person picks the true one, not the nearest label.
const FIELD_LABEL = {
  competitor: "Who did they choose?",
  reason: "Why (optional)",
  review_date: "Review date",
  stall_reason: "Why did the process stall?",
  our_reason: "Why are we stepping away?",
};

export function CloseOpportunityModal({ opportunity, open, onClose, onClosed }) {
  const [endings, setEndings] = useState([]);
  const [picked, setPicked] = useState(null);
  const [fields, setFields] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setPicked(null); setFields({}); setError(null);
    fetch(`${API}/opportunities/endings/options`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { endings: [] })).then((d) => setEndings(d.endings || []));
  }, [open]);

  const spec = endings.find((e) => e.id === picked);

  const submit = async () => {
    setSaving(true); setError(null);
    const res = await fetch(`${API}/opportunities/${opportunity.opportunity_id}/close`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ outcome: picked, ...fields }),
    });
    setSaving(false);
    if (res.ok) { onClosed && onClosed(await res.json()); onClose(); return; }
    const d = await res.json().catch(() => ({}));
    setError((d.detail && (d.detail.message || d.detail)) || "Could not close the opportunity.");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="close-opportunity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-slate-500" /> Close “{opportunity?.opportunity_name}”
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-500">How did it end? Each ending does something different.</p>

        <div className="space-y-2">
          {endings.map((e) => (
            <button key={e.id} onClick={() => { setPicked(e.id); setFields({}); setError(null); }}
              data-testid={`ending-${e.id}`}
              className={`w-full rounded-lg border p-3 text-left transition ${picked === e.id ? "border-slate-800 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{e.label}</span>
                {e.competitive_loss && <span className="text-[10px] font-medium text-red-600">counts as a loss</span>}
              </div>
              <p className="mt-1 text-xs text-slate-500">{e.successor}</p>
            </button>
          ))}
        </div>

        {spec && (
          <div className="space-y-3 border-t border-slate-100 pt-3">
            {[...(spec.needs || []), ...(spec.optional || [])].map((f) => (
              <div key={f}>
                <Label className="text-xs text-slate-600">
                  {FIELD_LABEL[f] || f}{(spec.needs || []).includes(f) ? " *" : ""}
                </Label>
                <Input type={f === "review_date" ? "date" : "text"}
                  value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })}
                  data-testid={`ending-field-${f}`} className="mt-1" />
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-red-600" data-testid="close-opportunity-error">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} data-testid="close-opportunity-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!picked || saving} data-testid="close-opportunity-confirm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Close deal"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
